import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import PaginatedResponse from '@/types/paginatedResponse';
import { QueryParams } from '@/types/query';
import RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

type Params = QueryParams;
type Host = string;

type ListReturnType<T> = RepositoryResponse<T[]> | RepositoryResponse<PaginatedResponse<T[]>>;

type GenericCrudActions<T, P> = {
  listBase: ({ host, params }: { host: Host; params: Params }) => Promise<ListReturnType<T>>;
  list: ({
    host,
    params
  }: {
    host: Host;
    params: Params;
  }) => Promise<RepositoryResponse<PaginatedResponse<T[]>>>;
  listNoPagination: ({
    host,
    params
  }: {
    host: Host;
    params: Params;
  }) => Promise<RepositoryResponse<T[]>>;
  get: ({ host, id }: { host: Host; id: string }) => Promise<RepositoryResponse<T>>;
  create: ({ host, data }: { host: Host; data: Partial<P> }) => Promise<T>;
  update: ({ host, id, data }: { host: Host; id: string; data: Partial<P> }) => Promise<T>;
  delete: ({ host, id }: { host: Host; id: string }) => Promise<void>;
};

export const createRepository = <T, P>(basePath: string): GenericCrudActions<T, P> => ({
  async listBase({ host, params }: { host: string; params: Params }): Promise<ListReturnType<T>> {
    try {
      const response = await ajax.get({ url: `${host}${basePath}/`, params });
      return { data: response.data, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, `Failed to list ${basePath}.`);
    }
  },

  async list({
    host,
    params
  }: {
    host: string;
    params: Params;
  }): Promise<RepositoryResponse<PaginatedResponse<T[]>>> {
    const response = await this.listBase({ host, params });
    return { data: response.data as PaginatedResponse<T[]>, isError: false };
  },

  async listNoPagination({
    host,
    params
  }: {
    host: string;
    params: Params;
  }): Promise<RepositoryResponse<T[]>> {
    const response = await this.listBase({ host, params });
    return { data: response.data as T[], isError: false };
  },

  async get({ host, id }: { host: string; id: string }): Promise<RepositoryResponse<T>> {
    try {
      const response = await ajax.get({ url: `${host}${basePath}/${id}/` });
      return { data: response.data, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, `Failed to get ${basePath}.`);
    }
  },

  async create({ host, data }: { host: string; data: Partial<P> }): Promise<T> {
    const response = await ajax.post({ url: `${host}${basePath}/`, data });
    return response.data as unknown as T;
  },

  async update({ host, id, data }: { host: string; data: Partial<P>; id: string }): Promise<T> {
    const response = await ajax.put({ url: `${host}${basePath}/${id}/`, data });
    return response.data as unknown as T;
  },

  async delete({ host, id }: { host: string; id: string }): Promise<void> {
    const response = await ajax.delete({ url: `${host}${basePath}/${id}/` });
    return response.data;
  }
});

const useGenericRepository = <T, P>(basePath: string) => {
  const { host } = useContext(AppContext);
  const repository = createRepository<T, P>(basePath);

  return {
    list: (params: Params = {}) => repository.list({ host, params }),
    listNoPagination: (params: Params = {}) => repository.listNoPagination({ host, params }),
    get: (id: string) => repository.get({ host, id }),
    create: (data: P) => repository.create({ host, data }),
    update: (id: string, data: P) => repository.update({ host, id, data }),
    delete: (id: string) => repository.delete({ host, id })
  };
};

export default useGenericRepository;
